import type { ComponentProps, ElementType, ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * An element that lifts into place when it first comes into view.
 *
 * It renders no client component of its own: it marks itself, and the one
 * observer in `Motion` picks it up. That keeps a page of eighty revealed
 * elements at one listener, and keeps every one of them server-rendered.
 */
type Props = {
    /** The element to render. Defaults to a div; headings and paragraphs pass their own. */
    as?: ElementType
    /** Milliseconds after the element enters the viewport, for staggering a row. */
    delay?: number
    className?: string
    children?: ReactNode
} & Omit<ComponentProps<'div'>, 'children' | 'className' | 'ref'>

export function Reveal({ as = 'div', delay = 0, className, children, ...props }: Props) {
    // Widened deliberately: `ElementType` is a union of every intrinsic tag,
    // and TypeScript resolves the props against all of them at once rather
    // than against the one this call passes — which types `children` as
    // `never` and every DOM handler as an SVG one.
    const Tag = as as ElementType<Record<string, unknown>>
    const attributes = props as Record<string, unknown>

    return (
        <Tag
            {...attributes}
            data-reveal="shown"
            data-reveal-delay={delay === 0 ? undefined : delay}
            className={cn('transition duration-700 ease-design', className)}
        >
            {children}
        </Tag>
    )
}
